/**
 * 영업용 체험(샘플) 계정 판정 상수. 화면 영역은 학생 마이페이지·교사 화면 전체.
 *
 * 샘플 계정은 DB에 계정 정보만 있고 풀이 기록이 없다. 리포트·반 종합·검사 대시보드는
 * FE 생성기(`src/sample-demo/`)가 만든 가짜 데이터를 보여준다.
 * 판정 기준은 로그인 ID 패턴 하나로 두어 학생·교사 모두 같은 규칙을 쓴다.
 * ★ 이 패턴에 걸리는 실제 계정을 만들면 그 계정의 진짜 데이터가 가려진다. ID 발급 시 주의.
 */

/** 샘플 계정 로그인 ID 패턴. haebomt1(교사), haebomsp01(초등 학생), haebomsm01(중등 학생). */
export const SAMPLE_LOGIN_ID_PATTERN = /^haebom(t|sp|sm)\d{1,2}$/;

/**
 * 샘플 학교명 접미사 후보. 교사 반 목록의 schoolName 판정에 보조로 쓴다.
 *
 * ★ 학교명은 관리자가 DB에서 바꿀 수 있다. 접미사 하나로 완전 일치 판정을 하면 이름이
 *   바뀌는 순간 교사 화면 샘플 리포트가 통째로 사라진다. 그래서 후보를 목록으로 두고,
 *   반 학생을 찾을 때는 접미사를 떼어 낸 앞부분(`sampleSchoolBaseName`)으로 맞춘다.
 */
export const SAMPLE_SCHOOL_NAME_SUFFIXES = ["(샘플용)", "(체험용)"] as const;

/** 로그인 성공 시 로그인 ID를 담는 sessionStorage 키. `pages/login.tsx`가 쓴다. */
export const LOGIN_ID_STORAGE_KEY = "loginId";

/** 샘플 계정 종류. 로그인 ID 접두어로 구분한다. */
export type SampleAccountKind = "teacher" | "student";

/** 로그인 ID가 샘플 패턴이면 종류를, 아니면 null을 돌려준다. */
export function resolveSampleKind(loginId: string | null | undefined): SampleAccountKind | null {
  if (!loginId || !SAMPLE_LOGIN_ID_PATTERN.test(loginId)) return null;
  return loginId.startsWith("haebomt") ? "teacher" : "student";
}

/** 학교명이 샘플 접미사 중 하나로 끝나면 true. */
export function isSampleSchoolName(schoolName: string | null | undefined): boolean {
  if (!schoolName) return false;
  return SAMPLE_SCHOOL_NAME_SUFFIXES.some((suffix) => schoolName.endsWith(suffix));
}

/**
 * 학교명에서 괄호 접미사를 떼고 앞부분만 돌려준다.
 *
 * "삐약초등학교(샘플용)"과 "삐약초등학교(체험용)"을 같은 학교로 보기 위한 비교 키다.
 * 반각 `(`과 전각 `（` 을 모두 자른다.
 *
 * @param schoolName 학교명. null이면 빈 문자열을 돌려준다.
 * @returns 접미사를 뗀 이름. 괄호가 없으면 원본 그대로(앞뒤 공백 제거).
 */
export function sampleSchoolBaseName(schoolName: string | null | undefined): string {
  if (!schoolName) return "";
  const cut = schoolName.search(/[(（]/);
  return (cut < 0 ? schoolName : schoolName.slice(0, cut)).trim();
}

/** 학생 마이페이지·교사 리포트 화면 공통 안내 문구. */
export const SAMPLE_REPORT_NOTICE =
  "영업용 체험 계정입니다. 표시되는 점수와 리포트는 실제 풀이 데이터가 아닌 샘플입니다.";

/** 실시간 검사 대시보드 안내 문구. 연출 길이는 `SAMPLE_EXAM_DURATION_SEC`와 맞춘다. */
export const SAMPLE_EXAM_NOTICE =
  "체험용 계정입니다. 이 화면의 검사 진행은 실제 학생 응시가 아니라 연출이며, 소개를 위해 실제 검사보다 빠르게(약 1분) 진행됩니다. 잠시 기다리시면 곧 실시간 검사 현황 진행 과정을 보실 수 있습니다.";

/** 검사 대시보드 연출 총 길이(초). 1분. 시뮬레이터의 모든 경과초 상수는 이 길이 안에 들어가야 한다. */
export const SAMPLE_EXAM_DURATION_SEC = 60;
